import axios, { AxiosInstance, AxiosResponse } from "axios";
import { IExternalQuote, IVehicle, IViolation, IQuoteQuestion } from "./ExternalQuote";
import { IRealTimeAutoResult, IPerson, IDriverBase, IDriver, IContactInfo, IPaymentBag, IViolationList, ICoverageConfig } from "./Domain";
import { successResponseInterceptor, errorResponseInterceptor } from "../infrastructure/global";
import { APP_PATH } from "../AppConstants";
import { getPortalSettings } from "../config/portalSettings";

export default class BravoApiClient {

    private _instance: AxiosInstance;

    constructor(stateCode: string | null = null) {
        // CA historically uses root Bravo endpoints (`/bravo/...`).
        // Other states use area full-quote endpoints (`/{state}/full/...`).
        // Note: `stateCode === "CA" ? null` must NOT fall through to `/full` — those routes do not exist.
        let baseURL: string;
        if (!stateCode || stateCode.toUpperCase() === "CA") {
            baseURL = APP_PATH + "/bravo";
        } else {
            baseURL = APP_PATH + "/" + stateCode.toLowerCase() + "/full";
        }

        this._instance = axios.create({
            baseURL,
            method: 'post'
        });

        this._instance.interceptors.response.use(
            successResponseInterceptor,
            errorResponseInterceptor
        );
    }

    createtQuote(selectedRate: IRealTimeAutoResult): Promise<AxiosResponse<any>> {
        return this._instance.request({
            url: '/create-quote',
            data: selectedRate
        });
    }

    createExternalQuote(tkn: string): Promise<AxiosResponse<any>> {
        return this._instance.request({
            url: '/create-external-quote/',
            data: { tkn: tkn }
        });
    }

    getQuote(quoteId: number): Promise<AxiosResponse<IExternalQuote>> {
        return this._instance.request({
            url: '/get-quote/' + quoteId,
            method: 'get'
        });
    }

    getLienholders(query: string) {
        return this._instance.request({
            url: '/get-lienholders?query=' + query,
            method: 'get'
        });
    }

    updateVehicle(quoteId: number, paymentPlanCode: number, vehicle: IVehicle, updateLienholder: boolean = false, updateGaragingAddress: boolean = false, addressSynchronization: boolean = false, stateCode: string | null = null) {
        const data = {
            quoteId,
            vehicleId: vehicle.id,
            vin: vehicle.vin,
            vehicleUse: vehicle.vehicleUse,
            paymentPlanCode,
            updateLienholder,
            hasLienholder: vehicle.hasLienholder,
            lienholderName: vehicle.lienholder?.name,
            lienholderAddress: vehicle.lienholder?.address?.street,
            lienholderCity: vehicle.lienholder?.address?.city,
            lienholderZip: vehicle.lienholder?.address?.zipCode,
            lienholderStateCd: vehicle.lienholder?.address?.stateCode,
            updateGaragingAddress,
            addressSynchronization,
            garagingStreet: vehicle.garagingAddress?.street,
            garagingApartment: vehicle.garagingAddress?.apartment,
            garagingCity: vehicle.garagingAddress?.city,
            garagingState: vehicle.garagingAddress?.stateCode,
            garagingZipcode: vehicle.garagingAddress?.zipCode,
            wasSalvaged: vehicle.wasSalvaged,
            stateCode
        };
        return this._instance.request({
            url: '/update-vehicle',
            data
        });
    }

    getCoverages(quoteId: number, coverageConfig: ICoverageConfig) {
        const data = {
            quoteId,
            liabilityCoverages: coverageConfig.liabilityCoverages,
            vehicleCoverages: coverageConfig.vehicleCoverages,
            addonCoverages: coverageConfig.addonCoverages
        }
        return this._instance.request({
            url: '/get-coverages',
            data
        });
    }

    updateCoverages(quoteId: number, paymentPlanCode: number, coverageConfig: ICoverageConfig) {
        const data = {
            quoteId,
            paymentPlanCode,
            liabilityCoverages: coverageConfig.liabilityCoverages,
            vehicleCoverages: coverageConfig.vehicleCoverages,
            addonCoverages: coverageConfig.addonCoverages
        }
        return this._instance.request({
            url: '/update-coverages',
            data
        });
    }

    updateDriver(quoteId: number, paymentPlanCode: number, driver: IDriver) {
        const data = {
            quoteId,
            paymentPlanCode,
            driver: {
                id: driver.id,
                isExcluded: driver.isExcluded,
                firstName: driver.firstName,
                lastName: driver.lastName,
                licenseNumber: driver.licenseNumber,
                licenseState: driver.licenseState,
                currentLicenseStatus: driver.currentLicenseStatus,
                birthDate: driver.birthDate,
                gender: driver.gender,
                maritalStatus: driver.maritalStatus,
                relationshipLevel: driver.relationshipLevel,
                isInsured: driver.isInsured,
                defensiveDriver: driver.defensiveDriver
            }
        };
        return this._instance.request({
            url: '/update-driver',
            data
        });
    }

    savePaymentInfo(quoteId: number, methodPaymentCd: string, paymentInfoBag: IPaymentBag) {
        const data = { 
            quoteId: parseInt(quoteId.toString()),
            methodPaymentCd: methodPaymentCd,
            downPaymentInstrument: paymentInfoBag?.downPaymentInstrument,
            InstallmentInstrument: paymentInfoBag?.installmentInstrument
        };

        return this._instance.request({
            url: '/save-payment-info',
            data
        });
    }

    getViolations(quoteId: number) {
        return this._instance.request({
            url: '/get-violations/' + quoteId,
            method: 'get'
        });
    }

    /**
     * Qualitas: GET {apiHostUrl}/d2c/ViolationList/{state} (PPA, D2CReferrerCheck — same allowlist as USPS).
     */
    getViolationList(state: string): Promise<AxiosResponse<IViolationList>> {
        // Qualitas: catalog from PPA (d2c/ViolationList), same host allowlist as USPS.
        const ppa = axios.create({
            baseURL: getPortalSettings().apiHostUrl,
            method: 'get'
        });
        ppa.interceptors.response.use(
            successResponseInterceptor,
            errorResponseInterceptor
        );
        return ppa.request({
            url: '/d2c/ViolationList/' + encodeURIComponent(state || ''),
            method: 'get'
        });
    }

    /** Qualitas: GET {apiHostUrl}/d2c/Mileage/ByZip?zip=&country= (same allowlist as USPS). */
    getDefaultMilesByZip(zip: string, country: string = 'USA'): Promise<AxiosResponse<{ miles: number | null }>> {
        const ppa = axios.create({
            baseURL: getPortalSettings().apiHostUrl,
            method: 'get'
        });
        ppa.interceptors.response.use(
            successResponseInterceptor,
            errorResponseInterceptor
        );
        return ppa.request({
            url: '/d2c/Mileage/ByZip',
            method: 'get',
            params: { zip: zip || '', country: country || 'USA' }
        });
    }

    reRateQuote(quoteId: number, paymentPlanCode: number) {
        return this._instance.request({
            url: '/re-rate',
            data: {
                quoteId: parseInt(quoteId.toString(), 10),
                paymentPlanCode
            }
        });
    }

    reSetQuoteEffDate(quoteId: number, paymentPlanCode: number, effectiveDate: string) {
        return this._instance.request({
            url: '/reset-eff-date',
            data: {
                quoteId: parseInt(quoteId.toString(), 10),
                paymentPlanCode,
                effectiveDate
            }
        });
    }

    getPaymentInfo(quoteId: number) {
        return this._instance.request({
            url: '/get-pmt-info/' + quoteId,
            method: 'get'
        });
    }

    disputeViolations(quoteId: number, driverId: number, violationsForDispute: Array<IViolation>) {
        const data = {
            quoteId: parseInt(quoteId.toString()),
            driverId,
            violationsForDispute
        };

        return this._instance.request({
            url: '/dispute-violations',
            data
        });
    }

    bindQuote(quoteId: number) {
        return this._instance.request({
            url: '/bind-quote/' + quoteId,
            method: 'get'
        });
    }

    getDocuments(quoteId: number) {
        return this._instance.request({
            url: '/get-documents/' + quoteId,
            method: 'get'
        });
    }

    getVehicle(quoteId: number, vehicleId: number) {
        return this._instance.request({
            url: '/get-vehicle/' + quoteId + '/' + vehicleId,
            method: 'get'
        });
    }

    getQuoteQuestions(rtrQuoteId: number) {
        return this._instance.request({
            url: '/get-quote-questions/' + rtrQuoteId,
            method: 'get'
        });
    }

    updateQuoteQuestions(quoteId: number, questions: Array<IQuoteQuestion>) {
        const data = {
            quoteId,
            questions
        };

        return this._instance.request({
            url: '/update-questions',
            data
        });
    }

    getESignatureForms(rtrQuoteId: number) {
        return this._instance.request({
            url: '/get-esig-forms/' + rtrQuoteId,
            method: 'get'
        });
    }

    updateESignatureAgreement(rtrQuoteId: number, esignatureCode: string, insuredName: string, needToSign: boolean, lastForm: boolean, options: any[]) {

        const data = {
            rtrQuoteId,
            code: esignatureCode,
            insuredName,
            needToSign,
            lastForm,
            options
        };

        return this._instance.request({
            url: '/update-esig-agreement',
            data
        });
    }


    updateContactInfo(quoteId: number, contactInfo: IContactInfo) {
        return this._instance.request({
            url: '/update-contact-fq',
            data: {
                quoteId,
                email: contactInfo.email,
                phone: contactInfo.phone
            }
        });
    }

    updateApplicantMailingAddress(quoteId: number, applicant: IPerson, contactInfo: IContactInfo) {
        return this._instance.request({
            url: '/update-address-fq',
            data: {
                quoteId,
                mailingStreet: applicant.address?.street,
                mailingApartment: applicant.address?.apartment,
                mailingCity: applicant.address?.city,
                mailingState: applicant.address?.stateCode,
                mailingZipcode: applicant.address?.zipCode,
                email: contactInfo?.email,
                Phone: contactInfo?.phone
            }
        });
    }

    updateAdditionalDrivers(quoteId: number, paymentPlanCode: number, additionalDrivers: Array<IDriver>) {
        return this._instance.request({
            url: '/update-additional-drivers',
            data: {
                quoteId,
                paymentPlanCode,
                additionalDrivers: additionalDrivers.map((x) => (
                    {
                        id: x.id,
                        isExcluded: x.isExcluded,
                        firstName: x.firstName,
                        lastName: x.lastName,
                        birthDate: x.birthDate,
                        gender: x.gender,
                        maritalStatus: x.maritalStatus,
                        licenseNumber: x.licenseNumber,
                        licenseState: x.licenseState,
                        currentLicenseStatus: x.currentLicenseStatus,
                        relationshipLevel: x.relationshipLevel
                    }
                ))
            }
        });
    }

    validateQuotePostData() {
        return Promise.reject();
    }

    postCreateQuote(program: string) {
        return Promise.reject();
    }
}
